/*
 *  Kanlibs/KAN_SigninNext.js
 *  Project: Kanoogi
 *
 *  One place that builds a "/signin and then come back here" URL.
 *
 *  WHY THIS EXISTS
 *
 *  The sign-in page has ALWAYS been able to return you somewhere:
 *  signin.js reads ?next= and KanoogiMain/src/signinDestination.js resolves it,
 *  preserving pathname + search + hash for any same-origin target. Nothing ever
 *  filled it in. Every entry point linked to a bare '/signin', so
 *  ResolveSigninDestination(null, ...) hit its `return('/home')` fallback and
 *  every signed-out visitor was deposited on /home no matter where they had
 *  been headed.
 *
 *  The sharpest version of that: a KanChat notification email deep-links to
 *  /convo?forum=…&topic=…&comment=… — a URL that WORKS, verified — but mail
 *  clients routinely open links in a browser with no session. The user signed
 *  in, landed on /home, and the forum/topic/comment were gone. The link looked
 *  broken; only the return trip ever was.
 *
 *  Two traps this centralises away:
 *    - location.pathname is NOT enough. It drops ?forum=…&topic=…&comment=…,
 *      which is the entire payload of a deep link. It must be
 *      pathname + search + hash. (sets.js was passing pathname only.)
 *    - the parameter is ?next=, not ?return=. KAN_LoginPrompt.js sent ?return=,
 *      which signin.js never reads — indistinguishable from sending nothing.
 */

"use strict";

// Pages where "return here after signing in" is meaningless or actively wrong:
// bouncing back to the sign-in flow itself would loop, and /create + /finish are
// mid-flow states whose URLs carry one-shot tokens.
const NO_RETURN=/^\/(signin|create|finish|notice|signout)(\/|$)/i;

// SigninHref() - '/signin' plus a ?next= that brings the user back to `target`
// (default: wherever they are right now, query string and hash included).
//
// The value is left relative and same-origin; ResolveSigninDestination()
// re-validates it on arrival and falls back to /home for anything suspicious,
// so this is the convenience half and that is the safety half.

export function SigninHref(target)
	{
	let here=target;
	if (!here)
		{
		try { here=window.location.pathname+window.location.search+window.location.hash; }
		catch (err) { return('/signin'); }
		}
	let path=String(here||'');
	try { path=new URL(path,window.location.origin).pathname; }
	catch (err) { /* keep the raw string; the test below is a best-effort guard */ }
	if (!here || NO_RETURN.test(path)) return('/signin');
	return(`/signin?next=${encodeURIComponent(here)}`);
	}
/* ---------------------------------------------------------------- */

// GoToSignin() - Navigate to sign-in, remembering where we were.

export function GoToSignin(target)
	{
	window.location.href=SigninHref(target);
	}
/* ---------------------------------------------------------------- */

// ApplySigninHref() - Point an existing anchor (the navbar "Sign in" link is
// static markup in navbar.html) at the return-preserving URL. No-op when the
// element is absent, so callers don't need their own guard.

export function ApplySigninHref(el)
	{
	if (!el || !el.setAttribute) return;
	el.setAttribute('href',SigninHref());
	}
